import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Loader2, Search, FileClock, Users, ListTree, Pencil, Check, X, Plus, Pencil as EditModeIcon, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import HOSupportMonthlyClaimed from './HOSupportMonthlyClaimed';

const enconOrange = '#ee7203';

const pct = (v) => (v === null || v === undefined || v === '') ? '—' : `${(v * 100).toFixed(1)}%`;

const formatDateDDMMYY = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
};

// Collapses rows that share the same key (e.g. account+supplier) AND the same
// first-3-chars of their group code into a single row showing that 3-char
// prefix as the group — e.g. 53SW + 53SM (same supplier) -> just "53S".
// Only used for the read-only (Edit Mode off) display — Edit Mode always
// shows every raw row individually so nothing is hidden from editing.
function collapseByGroupPrefix(rows, keyFields) {
  const buckets = new Map();
  const order = [];
  for (const r of rows) {
    const prefix = (r.group || '').slice(0, 3);
    const key = [...keyFields.map((f) => r[f]), prefix].join('|');
    if (!buckets.has(key)) {
      buckets.set(key, { ...r, _groupCount: 1 });
      order.push(key);
    } else {
      buckets.get(key)._groupCount += 1;
    }
  }
  return order.map((key) => {
    const bucket = buckets.get(key);
    if (bucket._groupCount > 1) bucket.group = (bucket.group || '').slice(0, 3);
    return bucket;
  });
}

const SUB_TABS = [
  { key: 'new_claims',      label: 'New Claims',      icon: FileClock },
  { key: 'live_accounts',   label: 'Live Accounts',    icon: Users },
  { key: 'supplier_matrix', label: 'Supplier Matrix',  icon: ListTree },
  { key: 'monthly_claimed', label: 'Monthly Claimed',  icon: CalendarCheck },
];

const STATUS_STYLE = {
  open:              { label: 'Open',              background: '#e5e7eb', color: '#374151' },
  promoted:          { label: 'Live (Claiming)',   background: '#dcfce7', color: '#15803d' },
  flagged_unclaimed: { label: 'Not Receiving',      background: '#fee2e2', color: '#b91c1c' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.open;
  return (
    <span
      className="text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap"
      style={{ background: s.background, color: s.color }}
      data-testid={`ho-support-status-badge-${status}`}
    >
      {s.label}
    </span>
  );
}

function EmptyOrLoading({ loading, empty, colSpan, emptyLabel }) {
  if (loading) {
    return (
      <tr><td colSpan={colSpan} className="text-center py-14">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: enconOrange }} />
      </td></tr>
    );
  }
  if (empty) {
    return <tr><td colSpan={colSpan} className="text-center py-14 text-gray-400">{emptyLabel}</td></tr>;
  }
  return null;
}

function TextField({ value, onChange, className = '', placeholder }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`border border-gray-300 rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-orange-300 ${className}`}
    />
  );
}

function PctField({ value, onChange }) {
  const display = value === null || value === undefined || value === '' ? '' : (value * 100).toFixed(1);
  return (
    <input
      type="number"
      step="0.1"
      value={display}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === '' ? null : parseFloat(raw) / 100);
      }}
      placeholder="e.g. 7.0"
      className="border border-gray-300 rounded px-2 py-1 text-xs w-20 focus:outline-none focus:ring-1 focus:ring-orange-300"
    />
  );
}

function SelectField({ value, onChange, options }) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="border border-gray-300 rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-orange-300"
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function RowActionCell({ isEditing, onEdit, onSave, onCancel, saving }) {
  if (isEditing) {
    return (
      <td className="px-2 py-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <button onClick={onSave} disabled={saving} className="text-green-600 hover:text-green-700 disabled:opacity-50" data-testid="ho-support-row-save-btn" title="Save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" data-testid="ho-support-row-cancel-btn" title="Cancel">
            <X className="w-4 h-4" />
          </button>
        </div>
      </td>
    );
  }
  return (
    <td className="px-2 py-3 whitespace-nowrap">
      <button onClick={onEdit} className="text-gray-400 hover:text-orange-500" data-testid="ho-support-row-edit-btn" title="Edit">
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </td>
  );
}

function AddRowButton({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      data-testid="ho-support-add-row-btn"
      className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white transition-colors"
      style={{ backgroundColor: enconOrange }}
    >
      <Plus className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

const NEW_CLAIM_BLANK = { month: '', date_claimed: '', account_number: '', type: 'Other', supplier_code: '', group: '', support_pct: null, status: 'open' };

function NewClaimsTable({ rows, loading, search, editMode, onChanged }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState(NEW_CLAIM_BLANK);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? rows.filter((r) =>
      (r.account_number || '').toLowerCase().includes(q) ||
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.supplier_code || '').toLowerCase().includes(q) ||
      (r.group || '').toLowerCase().includes(q) ||
      (r.type || '').toLowerCase().includes(q)
    ) : rows;
    return editMode ? base : collapseByGroupPrefix(base, ['account_number', 'supplier_code']);
  }, [rows, search, editMode]);

  const startEdit = (r) => { setEditingId(r.id); setDraft({ ...r }); };
  const cancelEdit = () => { setEditingId(null); setDraft({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`${API}/order-book-reports/ho-support/new-claims/${editingId}`, {
        month: draft.month, date_claimed: draft.date_claimed, account_number: draft.account_number,
        type: draft.type, supplier_code: draft.supplier_code, group: draft.group,
        support_pct: draft.support_pct, status: draft.status,
      });
      toast.success('Claim updated');
      cancelEdit();
      onChanged();
    } catch {
      toast.error('Failed to update claim');
    } finally {
      setSaving(false);
    }
  };

  const saveNew = async () => {
    if (!newRow.account_number || !newRow.supplier_code || !newRow.group) {
      toast.error('Account, Supplier and Group are required');
      return;
    }
    setSaving(true);
    try {
      await api.post(`${API}/order-book-reports/ho-support/new-claims/manual`, newRow);
      toast.success('Claim added');
      setAdding(false);
      setNewRow(NEW_CLAIM_BLANK);
      onChanged();
    } catch {
      toast.error('Failed to add claim');
    } finally {
      setSaving(false);
    }
  };

  const colCount = 9;

  return (
    <div>
      {editMode && (
        <div className="flex justify-end px-1 pb-2">
          <AddRowButton onClick={() => setAdding((v) => !v)} label="Add Claim" />
        </div>
      )}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ho-support-new-claims-table">
            <thead>
              <tr style={{ backgroundColor: enconOrange }}>
                {['Month', 'Date Claimed', 'Account', 'Type', 'Supplier', 'Group', 'Support %', 'Status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-white uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adding && (
                <tr className="bg-orange-50/60" data-testid="ho-support-new-claim-add-row">
                  <td className="px-2 py-2"><TextField value={newRow.month} onChange={(v) => setNewRow({ ...newRow, month: v })} placeholder="Sep-26" /></td>
                  <td className="px-2 py-2"><input type="date" value={newRow.date_claimed} onChange={(e) => setNewRow({ ...newRow, date_claimed: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" /></td>
                  <td className="px-2 py-2"><TextField value={newRow.account_number} onChange={(v) => setNewRow({ ...newRow, account_number: v })} placeholder="Account" /></td>
                  <td className="px-2 py-2"><SelectField value={newRow.type} onChange={(v) => setNewRow({ ...newRow, type: v })} options={['Other', 'PIR']} /></td>
                  <td className="px-2 py-2"><TextField value={newRow.supplier_code} onChange={(v) => setNewRow({ ...newRow, supplier_code: v })} placeholder="Supplier" /></td>
                  <td className="px-2 py-2"><TextField value={newRow.group} onChange={(v) => setNewRow({ ...newRow, group: v })} placeholder="Group" /></td>
                  <td className="px-2 py-2"><PctField value={newRow.support_pct} onChange={(v) => setNewRow({ ...newRow, support_pct: v })} /></td>
                  <td className="px-2 py-2"><SelectField value={newRow.status} onChange={(v) => setNewRow({ ...newRow, status: v })} options={['open', 'promoted', 'flagged_unclaimed']} /></td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <button onClick={saveNew} disabled={saving} className="text-green-600 hover:text-green-700 disabled:opacity-50" data-testid="ho-support-new-claim-add-save"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setAdding(false)} className="text-gray-400 hover:text-gray-600" data-testid="ho-support-new-claim-add-cancel"><X className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              )}
              <EmptyOrLoading loading={loading} empty={!loading && filtered.length === 0 && !adding} colSpan={colCount} emptyLabel="No new claims" />
              {!loading && filtered.map((r) => {
                const isEditing = editMode && editingId === r.id;
                return (
                  <tr key={`${r.id || r.account_number}-${r.supplier_code}-${r.group}`} className="border-t border-gray-100 hover:bg-orange-50/40 transition-colors" data-testid={`ho-support-new-claim-row-${r.account_number}`}>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{isEditing ? <TextField value={draft.month} onChange={(v) => setDraft({ ...draft, month: v })} /> : (r.month || '—')}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap" data-testid="ho-support-new-claim-date">
                      {isEditing
                        ? <input type="date" value={draft.date_claimed || ''} onChange={(e) => setDraft({ ...draft, date_claimed: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                        : formatDateDDMMYY(r.date_claimed)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isEditing ? (
                        <TextField value={draft.account_number} onChange={(v) => setDraft({ ...draft, account_number: v })} />
                      ) : (
                        <>
                          <span className="font-mono font-semibold text-gray-800">{r.account_number}</span>
                          {r.customer_name && <span className="text-gray-500 font-normal ml-2">— {r.customer_name}</span>}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{isEditing ? <SelectField value={draft.type} onChange={(v) => setDraft({ ...draft, type: v })} options={['Other', 'PIR']} /> : (r.type || '—')}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{isEditing ? <TextField value={draft.supplier_code} onChange={(v) => setDraft({ ...draft, supplier_code: v })} /> : r.supplier_code}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{isEditing ? <TextField value={draft.group} onChange={(v) => setDraft({ ...draft, group: v })} /> : r.group}</td>
                    <td className="px-4 py-3 text-gray-700">{isEditing ? <PctField value={draft.support_pct} onChange={(v) => setDraft({ ...draft, support_pct: v })} /> : pct(r.support_pct)}</td>
                    <td className="px-4 py-3">{isEditing ? <SelectField value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} options={['open', 'promoted', 'flagged_unclaimed']} /> : <StatusBadge status={r.status} />}</td>
                    {editMode ? (
                      <RowActionCell isEditing={isEditing} onEdit={() => startEdit(r)} onSave={saveEdit} onCancel={cancelEdit} saving={saving} />
                    ) : <td />}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const LIVE_ACCOUNT_BLANK = { account_number: '', customer_name: '', supplier_code: '', group: '', support_pct: null, product_scope: '' };

function LiveAccountsTable({ rows, loading, search, editMode, onChanged }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState(LIVE_ACCOUNT_BLANK);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? rows.filter((r) =>
      (r.account_number || '').toLowerCase().includes(q) ||
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.supplier_code || '').toLowerCase().includes(q) ||
      (r.group || '').toLowerCase().includes(q)
    ) : rows;
    return editMode ? base : collapseByGroupPrefix(base, ['account_number', 'supplier_code']);
  }, [rows, search, editMode]);

  const startEdit = (r) => { setEditingId(r.id); setDraft({ ...r }); };
  const cancelEdit = () => { setEditingId(null); setDraft({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`${API}/order-book-reports/ho-support/live-accounts/${editingId}`, {
        account_number: draft.account_number, customer_name: draft.customer_name,
        supplier_code: draft.supplier_code, group: draft.group,
        support_pct: draft.support_pct, product_scope: draft.product_scope,
      });
      toast.success('Live account updated');
      cancelEdit();
      onChanged();
    } catch {
      toast.error('Failed to update live account');
    } finally {
      setSaving(false);
    }
  };

  const saveNew = async () => {
    if (!newRow.account_number || !newRow.supplier_code || !newRow.group) {
      toast.error('Account, Supplier and Group are required');
      return;
    }
    setSaving(true);
    try {
      await api.post(`${API}/order-book-reports/ho-support/live-accounts/manual`, newRow);
      toast.success('Live account added');
      setAdding(false);
      setNewRow(LIVE_ACCOUNT_BLANK);
      onChanged();
    } catch {
      toast.error('Failed to add live account');
    } finally {
      setSaving(false);
    }
  };

  const colCount = 7;

  return (
    <div>
      {editMode && (
        <div className="flex justify-end px-1 pb-2">
          <AddRowButton onClick={() => setAdding((v) => !v)} label="Add Live Account" />
        </div>
      )}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ho-support-live-accounts-table">
            <thead>
              <tr style={{ backgroundColor: enconOrange }}>
                {['Account', 'Customer', 'Supplier', 'Group', 'Support %', 'Product / Scope', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-white uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adding && (
                <tr className="bg-orange-50/60" data-testid="ho-support-live-account-add-row">
                  <td className="px-2 py-2"><TextField value={newRow.account_number} onChange={(v) => setNewRow({ ...newRow, account_number: v })} placeholder="Account" /></td>
                  <td className="px-2 py-2"><TextField value={newRow.customer_name} onChange={(v) => setNewRow({ ...newRow, customer_name: v })} placeholder="Customer" /></td>
                  <td className="px-2 py-2"><TextField value={newRow.supplier_code} onChange={(v) => setNewRow({ ...newRow, supplier_code: v })} placeholder="Supplier" /></td>
                  <td className="px-2 py-2"><TextField value={newRow.group} onChange={(v) => setNewRow({ ...newRow, group: v })} placeholder="Group" /></td>
                  <td className="px-2 py-2"><PctField value={newRow.support_pct} onChange={(v) => setNewRow({ ...newRow, support_pct: v })} /></td>
                  <td className="px-2 py-2"><TextField value={newRow.product_scope} onChange={(v) => setNewRow({ ...newRow, product_scope: v })} placeholder="Scope" /></td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <button onClick={saveNew} disabled={saving} className="text-green-600 hover:text-green-700 disabled:opacity-50" data-testid="ho-support-live-account-add-save"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setAdding(false)} className="text-gray-400 hover:text-gray-600" data-testid="ho-support-live-account-add-cancel"><X className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              )}
              <EmptyOrLoading loading={loading} empty={!loading && filtered.length === 0 && !adding} colSpan={colCount} emptyLabel="No live accounts" />
              {!loading && filtered.map((r) => {
                const isEditing = editMode && editingId === r.id;
                return (
                  <tr key={`${r.id || r.account_number}-${r.supplier_code}-${r.group}`} className="border-t border-gray-100 hover:bg-orange-50/40 transition-colors" data-testid={`ho-support-live-account-row-${r.account_number}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-gray-800 whitespace-nowrap">{isEditing ? <TextField value={draft.account_number} onChange={(v) => setDraft({ ...draft, account_number: v })} /> : r.account_number}</td>
                    <td className="px-4 py-3 text-gray-700">{isEditing ? <TextField value={draft.customer_name} onChange={(v) => setDraft({ ...draft, customer_name: v })} /> : (r.customer_name || '—')}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{isEditing ? <TextField value={draft.supplier_code} onChange={(v) => setDraft({ ...draft, supplier_code: v })} /> : r.supplier_code}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{isEditing ? <TextField value={draft.group} onChange={(v) => setDraft({ ...draft, group: v })} /> : r.group}</td>
                    <td className="px-4 py-3 text-gray-700">{isEditing ? <PctField value={draft.support_pct} onChange={(v) => setDraft({ ...draft, support_pct: v })} /> : pct(r.support_pct)}</td>
                    <td className="px-4 py-3 text-gray-600">{isEditing ? <TextField value={draft.product_scope} onChange={(v) => setDraft({ ...draft, product_scope: v })} /> : (r.product_scope || '—')}</td>
                    {editMode ? (
                      <RowActionCell isEditing={isEditing} onEdit={() => startEdit(r)} onSave={saveEdit} onCancel={cancelEdit} saving={saving} />
                    ) : <td />}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const MATRIX_BLANK = { supplier_code: '', group: '', claim_range: '' };

function SupplierMatrixTable({ rows, loading, search, editMode, onChanged }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState(MATRIX_BLANK);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? rows.filter((r) =>
      (r.supplier_code || '').toLowerCase().includes(q) ||
      (r.group || '').toLowerCase().includes(q)
    ) : rows;
    return editMode ? base : collapseByGroupPrefix(base, ['supplier_code']);
  }, [rows, search, editMode]);

  const startEdit = (r) => { setEditingId(r.id); setDraft({ ...r }); };
  const cancelEdit = () => { setEditingId(null); setDraft({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`${API}/order-book-reports/ho-support/supplier-matrix/${editingId}`, {
        supplier_code: draft.supplier_code, group: draft.group, claim_range: draft.claim_range,
      });
      toast.success('Matrix entry updated');
      cancelEdit();
      onChanged();
    } catch {
      toast.error('Failed to update matrix entry');
    } finally {
      setSaving(false);
    }
  };

  const saveNew = async () => {
    if (!newRow.supplier_code || !newRow.group) {
      toast.error('Supplier and Group are required');
      return;
    }
    setSaving(true);
    try {
      await api.post(`${API}/order-book-reports/ho-support/supplier-matrix/manual`, newRow);
      toast.success('Matrix entry added');
      setAdding(false);
      setNewRow(MATRIX_BLANK);
      onChanged();
    } catch {
      toast.error('Failed to add matrix entry');
    } finally {
      setSaving(false);
    }
  };

  const colCount = 4;

  return (
    <div>
      {editMode && (
        <div className="flex justify-end px-1 pb-2">
          <AddRowButton onClick={() => setAdding((v) => !v)} label="Add Matrix Entry" />
        </div>
      )}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ho-support-supplier-matrix-table">
            <thead>
              <tr style={{ backgroundColor: enconOrange }}>
                {['Supplier', 'Group', 'Claim % / Range', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-white uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adding && (
                <tr className="bg-orange-50/60" data-testid="ho-support-matrix-add-row">
                  <td className="px-2 py-2"><TextField value={newRow.supplier_code} onChange={(v) => setNewRow({ ...newRow, supplier_code: v })} placeholder="Supplier" /></td>
                  <td className="px-2 py-2"><TextField value={newRow.group} onChange={(v) => setNewRow({ ...newRow, group: v })} placeholder="Group" /></td>
                  <td className="px-2 py-2"><TextField value={newRow.claim_range} onChange={(v) => setNewRow({ ...newRow, claim_range: v })} placeholder="e.g. 5.0% or 2.5%–3.0%" /></td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <button onClick={saveNew} disabled={saving} className="text-green-600 hover:text-green-700 disabled:opacity-50" data-testid="ho-support-matrix-add-save"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setAdding(false)} className="text-gray-400 hover:text-gray-600" data-testid="ho-support-matrix-add-cancel"><X className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              )}
              <EmptyOrLoading loading={loading} empty={!loading && filtered.length === 0 && !adding} colSpan={colCount} emptyLabel="No supplier matrix entries" />
              {!loading && filtered.map((r) => {
                const isEditing = editMode && editingId === r.id;
                return (
                  <tr key={`${r.id || r.supplier_code}-${r.group}`} className="border-t border-gray-100 hover:bg-orange-50/40 transition-colors" data-testid={`ho-support-matrix-row-${r.supplier_code}-${r.group}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-gray-800 whitespace-nowrap">{isEditing ? <TextField value={draft.supplier_code} onChange={(v) => setDraft({ ...draft, supplier_code: v })} /> : r.supplier_code}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{isEditing ? <TextField value={draft.group} onChange={(v) => setDraft({ ...draft, group: v })} /> : r.group}</td>
                    <td className="px-4 py-3 text-gray-700">{isEditing ? <TextField value={draft.claim_range} onChange={(v) => setDraft({ ...draft, claim_range: v })} /> : (r.claim_range || '—')}</td>
                    {editMode ? (
                      <RowActionCell isEditing={isEditing} onEdit={() => startEdit(r)} onSave={saveEdit} onCancel={cancelEdit} saving={saving} />
                    ) : <td />}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Head Office Support — BI Reports sub-view with three tabs (New Claims,
 * Live Accounts, Supplier Matrix) reading from the ho_support_* collections.
 * Edit Mode lets a user edit any field on any row or manually add a new row
 * on any tab; when off, rows sharing a supplier+group-prefix are collapsed
 * for a cleaner read-only view. Order Book's "Add H/O Claim" pill/modal
 * (Phase 2) writes into New Claims too — this is where those show up.
 */
export default function HOSupportView({ onOpenUpload, refreshSignal } = {}) {
  const [activeSubTab, setActiveSubTab] = useState('new_claims');
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newClaims, setNewClaims] = useState([]);
  const [liveAccounts, setLiveAccounts] = useState([]);
  const [supplierMatrix, setSupplierMatrix] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ncRes, laRes, smRes] = await Promise.all([
        api.get(`${API}/order-book-reports/ho-support/new-claims`),
        api.get(`${API}/order-book-reports/ho-support/live-accounts`),
        api.get(`${API}/order-book-reports/ho-support/supplier-matrix`),
      ]);
      setNewClaims(ncRes.data?.claims || []);
      setLiveAccounts(laRes.data?.accounts || []);
      setSupplierMatrix(smRes.data?.matrix || []);
    } catch {
      setNewClaims([]);
      setLiveAccounts([]);
      setSupplierMatrix([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCount = newClaims.filter((c) => c.status === 'open').length;
  const flaggedCount = newClaims.filter((c) => c.status === 'flagged_unclaimed').length;

  return (
    <div>
      {/* Overview line */}
      <div className="flex items-center gap-4 px-1 py-3 text-sm text-gray-600 flex-wrap" data-testid="ho-support-overview">
        <span><span className="font-bold text-gray-900">{newClaims.length}</span> New Claims</span>
        <span className="text-gray-300">·</span>
        <span><span className="font-bold text-gray-900">{openCount}</span> Open</span>
        <span className="text-gray-300">·</span>
        <span><span className="font-bold text-red-600">{flaggedCount}</span> Not Receiving Support</span>
        <span className="text-gray-300">·</span>
        <span><span className="font-bold text-gray-900">{liveAccounts.length}</span> Live Accounts</span>
        <span className="text-gray-300">·</span>
        <span><span className="font-bold text-gray-900">{supplierMatrix.length}</span> Supplier Matrix Entries</span>
      </div>

      {/* Sub-tab pills + search + edit mode toggle */}
      <div className="flex items-center justify-between gap-3 px-1 pb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {SUB_TABS.map((t) => {
            const Icon = t.icon;
            const isActive = activeSubTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveSubTab(t.key)}
                data-testid={`ho-support-subtab-${t.key}`}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                  isActive ? 'text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
                style={isActive ? { backgroundColor: enconOrange } : undefined}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          {activeSubTab !== 'monthly_claimed' && (
            <>
              <button
                onClick={() => setEditMode((v) => !v)}
                data-testid="ho-support-edit-mode-toggle"
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                  editMode ? 'text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
                style={editMode ? { backgroundColor: '#15803d' } : undefined}
              >
                <EditModeIcon className="w-3.5 h-3.5" /> Edit Mode: {editMode ? 'On' : 'Off'}
              </button>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search account, supplier, group…"
                  data-testid="ho-support-search-input"
                  className="pl-8 pr-3 py-1.5 text-xs sm:text-sm rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 w-56"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {activeSubTab === 'new_claims' && <NewClaimsTable rows={newClaims} loading={loading} search={search} editMode={editMode} onChanged={load} />}
      {activeSubTab === 'live_accounts' && <LiveAccountsTable rows={liveAccounts} loading={loading} search={search} editMode={editMode} onChanged={load} />}
      {activeSubTab === 'supplier_matrix' && <SupplierMatrixTable rows={supplierMatrix} loading={loading} search={search} editMode={editMode} onChanged={load} />}
      {activeSubTab === 'monthly_claimed' && (
        <HOSupportMonthlyClaimed onOpenUpload={onOpenUpload} refreshSignal={refreshSignal} onDataChanged={load} />
      )}
    </div>
  );
}
